/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

import type { EuiComboBoxOptionOption } from '@elastic/eui'
import { EuiComboBox, EuiText, EuiFlexGroup, EuiFlexItem, EuiIcon } from '@elastic/eui'

import { useListDeploymentsQuery } from '@modules/cloud-lib/deployments/hooks'
import type { DeploymentsListResponse } from '@modules/cloud-api/v1/types'

type Deployment = DeploymentsListResponse['deployments'][number]

type Props = {
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  ['data-test-id']?: string
  filter?: (deployment: Deployment) => boolean
}

const DeploymentSelectField: React.FunctionComponent<Props> = ({
  value = null,
  onChange,
  placeholder,
  'data-test-id': dataTestId,
  filter,
}) => {
  const { formatMessage } = useIntl()
  const [searchValue, setSearchValue] = useState('')

  const { data, isLoading } = useListDeploymentsQuery()

  const options = useMemo(() => {
    let deployments = data?.deployments || []

    // Apply custom filter if provided
    if (filter) {
      deployments = deployments.filter(filter)
    }

    // Filter by search value
    if (searchValue) {
      const searchLower = searchValue.toLowerCase()
      deployments = deployments.filter(
        (deployment) =>
          deployment.name.toLowerCase().includes(searchLower) ||
          deployment.id.toLowerCase().includes(searchLower),
      )
    }

    return deployments.map((deployment) => ({
      label: deployment.name || deployment.id,
      value: deployment.id,
      key: deployment.id,
      deployment,
    }))
  }, [data, searchValue, filter])

  const selectedOptions = useMemo(() => {
    if (!value) {
      return []
    }

    const option = options.find((opt) => opt.value === value)
    return option ? [option] : []
  }, [options, value])

  const renderOption = (option: EuiComboBoxOptionOption<string>) => (
    <EuiFlexGroup direction='column' gutterSize='none'>
      <EuiFlexItem>
        <EuiFlexGroup gutterSize='s' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiIcon type='logoElasticsearch' size='m' />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size='s'>
              <strong>{option.label}</strong>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiText size='xs' color='subdued' style={{ paddingLeft: '28px' }}>
          ID: {option.value?.slice(0, 6)}...
        </EuiText>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  return (
    <EuiComboBox
      options={options}
      selectedOptions={selectedOptions}
      onChange={(newSelectedOptions) => {
        const newValue = newSelectedOptions.length > 0 ? newSelectedOptions[0]?.value || null : null
        onChange(newValue)
      }}
      onSearchChange={setSearchValue}
      optionMatcher={({ option, searchValue }) => {
        const searchLower = searchValue.toLowerCase()
        return (
          option.label.toLowerCase().includes(searchLower) ||
          (option.value?.toLowerCase().includes(searchLower) ?? false)
        )
      }}
      renderOption={renderOption}
      fullWidth={true}
      rowHeight={46}
      isClearable={true}
      isLoading={isLoading}
      singleSelection={{ asPlainText: true }}
      data-test-id={dataTestId}
      placeholder={
        placeholder ||
        formatMessage({
          id: 'deploymentSelectField.placeholder',
          defaultMessage: 'Select',
        })
      }
      aria-label={formatMessage({
        id: 'deploymentSelectField.ariaLabel',
        defaultMessage: 'Select deployment',
      })}
    />
  )
}

export default DeploymentSelectField
